import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Button from "../Buttons/Button";
import { useTranslationApp } from "../../lib/i18next";
import { useSelectorApp } from "../../lib/redux";
import { FEATURE_ADDON_KEYS } from "../../actions/featureAddons";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import useAgencyFeatureAddons from "../../hooks/useAgencyFeatureAddons";
import { useTemplatesModals } from "../../contexts/templatesModalsContext";
import {
  FEATURE_ADDON_FREE_TIER_ID,
  isAddonUnpaidRenewal,
  isFeatureAddonLimitsEligible,
} from "../../utils/featureAddonCatalog";
import {
  companyInformation,
  LINK_LANDING_CONTACT,
} from "../../utils/constants";
import {
  getMaxModalLayerZIndex,
  MODAL_LAYER_BASE,
  MODAL_LAYER_STEP,
} from "../../utils/modalLayer";
import {
  BUTTON_GO_TO_BILLING,
  BUTTON_YOU_HAVE_DOUBTS,
  FEATURE_ADDON_NAME_AUTOMATIONS,
  FEATURE_ADDON_NAME_COMPETITORS,
  FEATURE_ADDON_NAME_TEMPLATES,
  FEATURE_ADDON_PAYWALL_FREE,
  FEATURE_ADDON_PAYWALL_TIER_NAME,
  FEATURE_ADDON_UNPAID_AMOUNT,
  FEATURE_ADDON_UNPAID_PERIOD,
  FEATURE_ADDON_UNPAID_TITLE,
} from "../../i18n/keysTranslations";

const FEATURE_NAME_KEY = {
  [FEATURE_ADDON_KEYS.AUTOMATIONS]: FEATURE_ADDON_NAME_AUTOMATIONS,
  [FEATURE_ADDON_KEYS.TEMPLATES]: FEATURE_ADDON_NAME_TEMPLATES,
  [FEATURE_ADDON_KEYS.COMPETITORS]: FEATURE_ADDON_NAME_COMPETITORS,
};

const tierLabel = ({ tierId, t }) => {
  if (!tierId || tierId === FEATURE_ADDON_FREE_TIER_ID) {
    return t(FEATURE_ADDON_PAYWALL_FREE);
  }
  const match = String(tierId).match(/(\d+)$/);
  return t(FEATURE_ADDON_PAYWALL_TIER_NAME, {
    index: match ? match[1] : tierId,
  });
};

/**
 * Non-dismissible paywall for a feature whose renewal invoice failed.
 * Shown only on that feature's surfaces. Cancelled and free tiers stay on the normal limits.
 */
const FeatureAddonUnpaidPaywall = ({
  addonKey,
  active = true,
  onLeave = null,
}) => {
  const { t } = useTranslationApp();
  const navigate = useNavigate();
  const { closeTemplatesModals } = useTemplatesModals();
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const planPrice = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.price
  );
  const eligible =
    active &&
    isFeatureAddonLimitsEligible({ isAppSumo, isAppSumoResolved, planPrice });
  const { status, isLoading } = useAgencyFeatureAddons({
    addonKey,
    enabled: eligible,
  });

  const agencyAddon = status?.agencyAddon;
  const open = eligible && !isLoading && isAddonUnpaidRenewal(agencyAddon);

  const onGoToBilling = useCallback(() => {
    onLeave?.();
    closeTemplatesModals();
    navigate("/billing");
  }, [closeTemplatesModals, navigate, onLeave]);

  if (!open) return null;

  const feature = t(
    FEATURE_NAME_KEY[addonKey] || FEATURE_ADDON_NAME_AUTOMATIONS
  );
  const tier = tierLabel({ tierId: agencyAddon?.tierId, t });
  const unpaid = agencyAddon?.unpaidInvoice || null;
  const periodStart = Number(unpaid?.periodStart);
  const periodEnd = Number(unpaid?.periodEnd);
  const hasPeriod = Number.isFinite(periodStart) && Number.isFinite(periodEnd);
  const amount = Number(unpaid?.value);
  const topModalZIndex = getMaxModalLayerZIndex();
  const zIndex =
    (topModalZIndex > 0 ? topModalZIndex : MODAL_LAYER_BASE) + MODAL_LAYER_STEP;

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={() => {}}
      fullWidth
      maxWidth="sm"
      aria-labelledby="feature-addon-unpaid-title"
      sx={{ zIndex }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundImage: "none",
          maxWidth: 640,
        },
      }}
    >
      <Box
        sx={{
          p: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Typography
          id="feature-addon-unpaid-title"
          variant="h5"
          sx={{ textAlign: "center", fontWeight: 700, color: "text.primary" }}
        >
          {t(FEATURE_ADDON_UNPAID_TITLE, { feature, tier })}
        </Typography>

        {Number.isFinite(amount) ? (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "text.secondary", maxWidth: 480 }}
          >
            {t(FEATURE_ADDON_UNPAID_AMOUNT, {
              value: amount,
              currency: unpaid?.currency || "USD",
            })}
          </Typography>
        ) : null}

        {hasPeriod ? (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "text.secondary", maxWidth: 480 }}
          >
            {t(FEATURE_ADDON_UNPAID_PERIOD, {
              feature,
              tier,
              startDate: new Date(periodStart),
              endDate: new Date(periodEnd),
            })}
          </Typography>
        ) : null}

        <Button
          variant="contained"
          color="primary"
          onClick={onGoToBilling}
          sx={{ mt: 1, px: 4, py: 1.5, fontSize: "1rem", fontWeight: 600 }}
        >
          {t(BUTTON_GO_TO_BILLING)}
        </Button>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Link
            href={LINK_LANDING_CONTACT}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              mt: 2,
              textDecoration: "underline",
              color: "text.secondary",
              cursor: "pointer",
              fontSize: "0.875rem",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(BUTTON_YOU_HAVE_DOUBTS)}
          </Link>
          <Box
            sx={{
              mt: 1,
              textAlign: "center",
              borderTop: 1,
              borderColor: "divider",
              width: "100%",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                lineHeight: 1.6,
              }}
            >
              {companyInformation.name} - A Delaware LLC
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                lineHeight: 1.6,
              }}
            >
              {companyInformation.address}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FeatureAddonUnpaidPaywall;
